import { Component, OnInit } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';

import { translate } from '@jsverse/transloco';
import { FileItem, FileUploader } from 'ng2-file-upload';

import { FullStoryService, PageNames } from 'app/modules/full-story/services/full-story.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import { ProjectService } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { LoggerAdditional, LoggerService } from 'app/services/logger.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import {
  CollectionAdvancedOptions,
  CollectionAdvancedOptionsDialogComponent,
} from '../../components/collection-advanced-options-dialog/collection-advanced-options-dialog.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-collections-upload',
  templateUrl: './collection-upload.component.html',
  styleUrls: ['./collection-upload.component.scss'],
  standalone: false,
})
export class CollectionUploadComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private MB = 1048576;

  public selectedCollectionName: string | null = null;
  public selectedDisplayName: string | null = null;
  public projectId: string | null = null;
  public uploadId: string | null = null;
  public lastErrorMessage: string | null = null;
  public uploader: FileUploader;
  public hasBaseDropZoneOver = false;

  private allowedFileTypes: { string: string };
  public maxFileSizeMB: number;
  public maxFileSizeOverrides: { string: number };
  public advancedSettings: CollectionAdvancedOptions;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private collectionService: CollectionService,
    private groupService: GroupService,
    private projectService: ProjectService,
    private fullStoryService: FullStoryService,
    private logger: LoggerService,
    private dialogService: DialogService
  ) {}

  async ngOnInit() {
    const params = this.route.snapshot.paramMap;
    this.projectId = params.get('projectId');
    this.selectedCollectionName = params.get('collectionName');
    const queryParams = this.route.snapshot.queryParamMap;
    this.selectedDisplayName = queryParams.get('displayName');

    this.fullStoryService.setPageVars(PageNames.upload_file, {
      projectId: this.projectId,
      collectionId: this.selectedCollectionName,
    });

    const groupId = params.get('groupId');
    await this.groupService.refreshAndSelectActiveGroup(groupId);

    const url = 'http://will.be.changed.later/';
    this.uploader = new FileUploader({
      url,
      queueLimit: 1,
      autoUpload: false,
      authToken: this.collectionService.getToken(),
    });
    this.uploader.onErrorItem = this.onErrorItem.bind(this);
    this.uploader.onSuccessItem = this.onSuccessItem.bind(this);
    this.uploader.onAfterAddingFile = this.doUpload.bind(this);

    const appConfig = (window as any).appConfig;
    this.allowedFileTypes = appConfig.uploader.allowedFileTypes;
    this.maxFileSizeMB = appConfig.uploader.maxFileSizeMB;
    this.maxFileSizeOverrides = appConfig.uploader.maxFileSizeOverrides;
  }

  public getRouteToProject(): string {
    const route = `/groups/${this.groupService.getSelectedGroupId(true)}/projects/${
      this.projectId
    }`;
    return route;
  }

  // success handler
  public onSuccessItem(item: FileItem, response: string): void {
    if (!response || !response.startsWith('{')) {
      this.lastErrorMessage = translate('collection-upload.invalid-response-from-server');
      item.isError = true;
      item.isSuccess = false;
      return;
    }
    try {
      const payload = JSON.parse(response);
      this.uploadId = payload.id;
      this.router.navigate(
        [
          `groups/${this.groupService.getSelectedGroupId(true)}/projects/${
            this.projectId
          }/collections/${this.selectedCollectionName}/uploads/${payload.id}/map`,
        ],
        { queryParams: { displayName: this.selectedDisplayName } }
      );
    } catch (e) {
      this.lastErrorMessage = translate('collection-upload.invalid-json-response-from-server');
      item.isError = true;
      item.isSuccess = false;
    }
  }

  // error handler
  public onErrorItem(item: FileItem, response: string, status: number): void {
    this.lastErrorMessage = response;
    const additional: LoggerAdditional = {
      file: {
        name: item.file.name,
        size: item.file.size,
      },
      status,
    };
    if (!response) {
      this.lastErrorMessage = `HTTP code ${status}`;
    } else if (response.startsWith('{')) {
      const payload = JSON.parse(response);
      if (payload.type === 'UploadMultipartException') {
        this.lastErrorMessage = `${translate('collection-upload.not-able-to-upload')}: ${
          payload.description
        }`;
      } else if (payload.description) {
        this.lastErrorMessage = `${payload.type}: ${payload.description}`;
        if (payload.data) {
          this.lastErrorMessage += ` (${payload.data})`;
        }
      }
      additional.error = payload;
    } else {
      additional.response = response;
    }
    this.logger.error('UploadFailed', additional);
  }

  // kick off the upload
  public doUpload(item: FileItem): void {
    if (
      this.isValidFilename(item.file.name) &&
      this.isValidFilsesize(item.file.name, item.file.size)
    ) {
      item.url = `${this.collectionService.getBaseUrl()}/projects/${this.projectId}/collections/${
        this.selectedCollectionName
      }/uploads/`;
      if (this.advancedSettings) {
        let separator = '?';
        if (this.advancedSettings.toSkip) {
          item.url += `${separator}startRow=${this.advancedSettings.toSkip}`;
          separator = '&';
        }
        if (this.advancedSettings.toUpload) {
          item.url += `${separator}rowLimit=${this.advancedSettings.toUpload}`;
          separator = '&';
        }
      }
      item.upload();
    }
  }

  // when a file is added, check that it has a valid extension
  public validateSelectedFiles(files: FileList, fileInput: HTMLInputElement): void {
    const file: File = files[0];
    if (!this.isValidFilename(file.name)) {
      // not valid, don't allow it to be added
      this.alertService.showErrorToast(
        `${translate('collection-upload.invalid-file-extension-on')} ${file.name}`
      );
      this.uploader.queue.splice(0, 1);
      fileInput.value = '';
    } else if (!this.isValidFilsesize(file.name, file.size)) {
      this.alertService.showErrorToast(
        translate('collection-upload.uploads-must-be-smaller-than', {
          size: this.getMaxSize(file.name) / this.MB,
        })
      );
      this.uploader.queue.splice(0, 1);
      fileInput.value = '';
    }
  }

  // valididate filename by extension against allowed list
  public isValidFilename(filename: string): boolean {
    return this.getFiletype(filename) !== 'unknown';
  }

  public isValidFilsesize(filename: string, size: number): boolean {
    return size > 0 && size < this.getMaxSize(filename);
  }

  public getFiletype(filename: string): string {
    const ext = filename.substr(filename.lastIndexOf('.') + 1).toLowerCase();
    if (!this.allowedFileTypes[ext]) {
      return 'unknown';
    }
    return this.allowedFileTypes[ext];
  }

  public getMaxSize(filename: string): number {
    const filetype = this.getFiletype(filename);
    if (filetype === 'unknown') {
      return 0;
    } else if (this.maxFileSizeOverrides[filetype]) {
      return this.maxFileSizeOverrides[filetype] * this.MB;
    } else {
      return this.maxFileSizeMB * this.MB;
    }
  }

  public fileOverBase(e: boolean): void {
    this.hasBaseDropZoneOver = e;
  }

  public showAdvancedOptionsDialog(): void {
    this.dialogService
      .showDialog<CollectionAdvancedOptionsDialogComponent>(
        CollectionAdvancedOptionsDialogComponent,
        this.advancedSettings,
        {},
        false
      )
      .afterClosed()
      .subscribe((result) => {
        this.advancedSettings = result;
      });
  }
}
